import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, FolderOpen, Inbox, ExternalLink, Sliders, UserCog, BarChart3, ShieldCheck, Image as ImageIcon, PenTool, Share2 } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import DashboardPanel from "@/pages/admin/DashboardPanel";
import ContactRequestsPanel from "@/pages/admin/ContactRequestsPanel";
import PortfolioPanel from "@/pages/admin/PortfolioPanel";
import ContentPanel from "@/pages/admin/ContentPanel";
import AccountPanel from "@/pages/admin/AccountPanel";
import AuditLogPanel from "@/pages/admin/AuditLogPanel";
import MarketingImagesPanel from "@/pages/admin/MarketingImagesPanel";
import SignaturePanel from "@/pages/admin/SignaturePanel";
import OGImagePanel from "@/pages/admin/OGImagePanel";
import ThemeToggle from "@/components/ThemeToggle";

const TABS = [
  { id: "dashboard", icon: BarChart3, label_fr: "Tableau de bord", label_en: "Dashboard" },
  { id: "contact", icon: Inbox, label_fr: "Demandes", label_en: "Requests" },
  { id: "portfolio", icon: FolderOpen, label_fr: "Portfolio", label_en: "Portfolio" },
  { id: "marketing", icon: ImageIcon, label_fr: "Images marketing", label_en: "Marketing images" },
  { id: "signature", icon: PenTool, label_fr: "Signature", label_en: "Signature" },
  { id: "og", icon: Share2, label_fr: "Vignette partage", label_en: "Share thumbnail" },
  { id: "content", icon: Sliders, label_fr: "Contenu", label_en: "Content" },
  { id: "audit", icon: ShieldCheck, label_fr: "Sécurité", label_en: "Security" },
  { id: "account", icon: UserCog, label_fr: "Compte", label_en: "Account" },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const [tab, setTab] = useState("dashboard");
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-[#F2EBE5]">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(197,160,89,0.3)",
            color: "#F2EBE5",
          },
        }}
      />

      {/* Top bar */}
      <header className="border-b border-[#F2EBE5]/10 bg-[#050505]/95 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center">
              <img
                src="/brand/kiwami-logo.png"
                alt="Kiwami Studios & Co"
                className="h-7 w-auto select-none"
                draggable={false}
              />
            </Link>
            <span className="hidden md:inline-block h-5 w-px bg-[#C5A059]/40" />
            <span className="hidden md:inline-block overline !text-[10px]">
              {lang === "fr" ? "Administration" : "Admin"}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/"
              target="_blank"
              data-testid="admin-view-site"
              className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#A19E9C] hover:text-[#C5A059] transition-colors"
            >
              <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              {lang === "fr" ? "Voir le site" : "View site"}
            </Link>
            <ThemeToggle />
            <button
              type="button"
              data-testid="admin-lang-toggle"
              onClick={toggleLang}
              className="text-[11px] uppercase tracking-[0.3em] font-medium text-[#F2EBE5] hover:text-[#C5A059] transition-colors border border-[#F2EBE5]/15 px-3 py-1.5 rounded-full"
            >
              <span className={lang === "fr" ? "text-[#C5A059]" : ""}>FR</span>
              <span className="mx-2 text-[#F2EBE5]/30">/</span>
              <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
            </button>
            <span className="hidden md:inline text-xs text-[#A19E9C]">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="kw-btn-ghost"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              {lang === "fr" ? "Déconnexion" : "Logout"}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-10">
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Tableau de bord" : "Dashboard"}
        </p>
        <h1 className="font-serif-display text-4xl md:text-6xl mt-4 leading-[1]">
          {lang === "fr" ? "Bonjour, " : "Hello, "}
          <em className="text-[#C5A059]">{user?.name || "Admin"}</em>
        </h1>
        <p className="mt-3 text-[#A19E9C] text-sm">
          {lang === "fr"
            ? "Gérez les demandes entrantes et le portfolio depuis cette interface."
            : "Manage incoming requests and the portfolio from this interface."}
        </p>
      </section>

      {/* Tabs */}
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 mb-10 flex gap-1 overflow-x-auto border-b border-[#F2EBE5]/10">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex-shrink-0 flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-4 md:px-5 py-4 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-[#C5A059] text-[#C5A059]"
                  : "border-transparent text-[#A19E9C] hover:text-[#F2EBE5]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {lang === "fr" ? t.label_fr : t.label_en}
            </button>
          );
        })}
      </nav>

      {/* Panels */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
        {tab === "dashboard" && <DashboardPanel />}
        {tab === "contact" && <ContactRequestsPanel />}
        {tab === "portfolio" && <PortfolioPanel />}
        {tab === "marketing" && <MarketingImagesPanel />}
        {tab === "signature" && <SignaturePanel />}
        {tab === "og" && <OGImagePanel />}
        {tab === "content" && <ContentPanel />}
        {tab === "audit" && <AuditLogPanel />}
        {tab === "account" && <AccountPanel />}
      </main>
    </div>
  );
};

export default AdminDashboard;
